import { DocumentSnapshot, Timestamp, logger } from "../firestoreImports"
import { z } from "zod"
import { Car } from "./car"
import { Fleet } from "./fleet"
import { Location } from "./location"

export enum UserPriorityLevel {
    Banned = -5,
    Restricted = -3,
    Basic = 0,
    Higher = 1,
    Favored = 4,
    Carpooling = 6,
    Priority = 7,
    Visitors = 8,
    Permanent = 9,
    Admin = 10
}

export class UserPriorityLevelUtils {
    static allValues = () => {
        return Object.keys(UserPriorityLevel)
            .map(v => Number(v))
            .filter(v => !isNaN(v))
            .sort((a, b) => a - b)
    }

    static publicValues = () => {
        return UserPriorityLevelUtils.allValues().filter(v => [-5, -3, 0, 1, 4, 7].includes(v))
    }

    static toString = (priority: UserPriorityLevel): string => {
        return UserPriorityLevel[priority]
    }
}

const userPublicSchema = z
    .object({
        name: z.string().default(""),
        surname: z.string().default(""),
        email: z.string().default(""),
        company: z.string().default("unknown"),
        locations: z.string().array().nullish().default([]),
        fleets: z.string().array().nullish().default([]),
        locale: z.string().default("en"),
        deleted: z.boolean().default(false),
        anonymised: z.boolean().default(false),
        priority: z.enum(UserPriorityLevel).default(UserPriorityLevel.Basic),
        defaultCarId: z.string().nullish(),
        slackData: z.any().nullish(),
        profilePicture: z.string().nullish(),
        externalId: z.string().nullish(),
        scimId: z.string().nullish(),
        fcmPfToken: z.string().nullish(),
        lastActiveGarageWeb: z.custom<Timestamp>().nullish(),
        visitorCompany: z.string().nullish(),
        host: z.string().nullish(),
        fcmToken: z.string().nullish(),
        lastActiveCarpoolWeb: z.custom<Timestamp>().nullish(),
        visibleCompanies: z.string().array().default([]),
        /** @deprecated Deprecated in 2.0 */
        timestampUpdated: z.custom<Timestamp>().nullish(),
        timestampCreated: z.custom<Timestamp>().nullish(),
        isNewAuthFlowUsed: z.boolean().nullish()
    })
    .transform(data => ({
        ...data,
        locations: (data.locations ?? []).length > 0 ? data.locations : [data.company]
    }))

const userPrivateSchema = z.object({
    surname: z.string().default(""),
    phone: z.string().nullish(),
    email: z.string().default(""),
    timestampCreated: z.custom<Timestamp>().nullish(),
    /** @deprecated Deprecated in 2.0 */
    timestampUpdated: z.custom<Timestamp>().nullish()
})

export interface IUser {
    uid: string
    name: string
    surname: string
    email: string
    company: string
    locationIds: string[]
    fleetIds: string[]
    /** @deprecated Deprecated in 2.0 */
    activeLocationId: string
    locale: string
    deleted: boolean
    anonymised: boolean
    priority: UserPriorityLevel
    defaultCarId?: string
    slackData?: any
    profilePicture?: string
    externalId?: string
    scimId?: string
    fcmPfToken?: string
    lastActiveGarageWeb?: Date
    visitorCompany?: string
    host?: string
    fcmToken?: string
    lastActiveCarpoolWeb?: Date
    visibleCompanies: string[]
    timestampCreated?: Date
    /** @deprecated Deprecated in 2.0 */
    timestampUpdated?: Date
    phone?: string
    isNewAuthFlowUsed?: boolean
}

export interface User extends IUser {}
export class User {
    // Separately fetched data
    cars?: Car[]
    locations?: Location[]
    fleets?: Fleet[]
    adminLocations: string[] = []
    adminCompanies: string[] = []
    adminFleets: string[] = []
    get activeLocation() {
        return this.locations?.at(0)
    }

    constructor(params: IUser) {
        Object.assign(this, params)
    }

    static create(uid: string, name: string, surname: string, email: string): User {
        return new User({
            uid,
            name,
            surname,
            email,
            company: "unknown",
            locationIds: [],
            fleetIds: [],
            activeLocationId: "",
            locale: "en",
            deleted: false,
            anonymised: false,
            priority: UserPriorityLevel.Basic,
            visibleCompanies: []
        })
    }

    static fromData(doc: DocumentSnapshot): User | null {
        const raw = doc.data()
        if (!raw) {
            logger.warn(`Cannot create User ${doc.id} from snapshot: missing data`)
            return null
        }

        try {
            const parsed = userPublicSchema.parse(raw)
            const visibleCompanies = parsed.visibleCompanies.length > 0 ? parsed.visibleCompanies : [parsed.company]

            return new User({
                uid: doc.id,
                name: parsed.name,
                surname: parsed.surname,
                email: parsed.email,
                company: parsed.company,
                locationIds: parsed.locations ?? [parsed.company],
                fleetIds: parsed.fleets ?? [],
                activeLocationId: parsed.locations?.[0] ?? parsed.company,
                locale: parsed.locale,
                deleted: parsed.deleted,
                anonymised: parsed.anonymised,
                priority: parsed.priority,
                defaultCarId: parsed.defaultCarId ?? undefined,
                slackData: parsed.slackData ?? undefined,
                profilePicture: parsed.profilePicture ?? undefined,
                externalId: parsed.externalId ?? undefined,
                scimId: parsed.scimId ?? undefined,
                fcmPfToken: parsed.fcmPfToken ?? undefined,
                lastActiveGarageWeb: parsed.lastActiveGarageWeb?.toDate() ?? undefined,
                visitorCompany: parsed.visitorCompany ?? undefined,
                host: parsed.host ?? undefined,
                fcmToken: parsed.fcmToken ?? undefined,
                lastActiveCarpoolWeb: parsed.lastActiveCarpoolWeb?.toDate() ?? undefined,
                visibleCompanies,
                timestampCreated: parsed.timestampCreated?.toDate() ?? undefined,
                timestampUpdated: parsed.timestampUpdated?.toDate() ?? undefined,
                isNewAuthFlowUsed: parsed.isNewAuthFlowUsed ?? undefined
            })
        } catch (error) {
            logger.error(`Cannot create User ${doc.id} from snapshot:`, error)
            return null
        }
    }

    asDataPublic(): { [k: string]: any } {
        return userPublicSchema.parse({
            timestampCreated: this.timestampCreated ?? Timestamp.now(),
            timestampUpdated: Timestamp.now(),
            name: this.name,
            surname: this.surname.substring(0, 1),
            externalId: this.externalId,
            scimId: this.scimId,
            email: "",
            company: this.company,
            locations: this.locationIds,
            priority: this.priority,
            host: this.host,
            visitorCompany: this.visitorCompany,
            locale: this.locale,
            fleets: this.fleetIds,
            deleted: this.deleted,
            anonymised: this.anonymised,
            defaultCarId: this.defaultCarId,
            slackData: this.slackData,
            profilePicture: this.profilePicture,
            fcmPfToken: this.fcmPfToken,
            fcmToken: this.fcmToken,
            lastActiveGarageWeb: this.lastActiveGarageWeb,
            lastActiveCarpoolWeb: this.lastActiveCarpoolWeb,
            visibleCompanies: this.visibleCompanies.length > 0 ? this.visibleCompanies : [this.company]
        })
    }

    asDataPrivate(): { [k: string]: any } {
        return userPrivateSchema.parse({
            /** @deprecated Deprecated in 2.0 */
            timestampUpdated: Timestamp.now(),
            timestampCreated: this.timestampCreated ?? Timestamp.now(),
            surname: this.surname,
            phone: this.phone,
            email: this.email
        })
    }

    defaultCar() {
        if (!this.cars || this.cars.length === 0) {
            return null
        }
        const defaultCar = this.cars.find(c => c.id === this.defaultCarId)
        if (defaultCar) {
            return defaultCar
        }
        return this.cars[0]
    }
}
